import React, { useState } from 'react';

const TIME_SLOTS = [
  '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00',
  '19:00', '20:00', '21:00',
];

const ACTIVITY_TYPES = [
  { value: 'sightseeing', label: 'Sightseeing', color: '#3b82f6' },
  { value: 'food', label: 'Food & Dining', color: '#f59e0b' },
  { value: 'adventure', label: 'Adventure', color: '#ef4444' },
  { value: 'relaxation', label: 'Relaxation', color: '#10b981' },
  { value: 'culture', label: 'Culture', color: '#8b5cf6' },
  { value: 'shopping', label: 'Shopping', color: '#ec4899' },
  { value: 'transport', label: 'Transport', color: '#64748b' },
  { value: 'other', label: 'Other', color: '#94a3b8' },
];

export default function DailyItineraryPlanner({ tripDays = 5, activities = [], destination = '', onSave }) {
  const [days, setDays] = useState(() => {
    const initial = [];
    for (let i = 0; i < tripDays; i++) {
      initial.push({
        dayNumber: i + 1,
        label: `Day ${i + 1}`,
        items: activities.filter(a => a.day === i + 1).map(a => ({
          id: a.id || Math.random().toString(36).slice(2),
          time: a.time || '09:00',
          name: a.name || a.activity_name || '',
          type: a.type || 'sightseeing',
          duration: a.duration || '1 hour',
          notes: a.notes || '',
        })) || [],
      });
    }
    return initial;
  });
  const [activeDay, setActiveDay] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ time: '09:00', name: '', type: 'sightseeing', duration: '1 hour', notes: '' });

  const addActivity = () => {
    if (!newItem.name.trim()) return;
    const updated = [...days];
    updated[activeDay].items.push({
      id: Math.random().toString(36).slice(2),
      ...newItem,
    });
    updated[activeDay].items.sort((a, b) => a.time.localeCompare(b.time));
    setDays(updated);
    setNewItem({ time: '09:00', name: '', type: 'sightseeing', duration: '1 hour', notes: '' });
    setShowAddForm(false);
  };

  const removeActivity = (dayIdx, itemId) => {
    const updated = [...days];
    updated[dayIdx].items = updated[dayIdx].items.filter(i => i.id !== itemId);
    setDays(updated);
  };

  const moveActivity = (dayIdx, itemIdx, direction) => {
    const updated = [...days];
    const items = updated[dayIdx].items;
    const target = itemIdx + direction;
    if (target < 0 || target >= items.length) return;
    [items[itemIdx], items[target]] = [items[target], items[itemIdx]];
    setDays(updated);
  };

  const getTypeInfo = (type) => ACTIVITY_TYPES.find(t => t.value === type) || ACTIVITY_TYPES[7];

  const handleSave = () => {
    if (onSave) onSave(days);
  };

  return (
    <div data-testid="daily-itinerary-planner" style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-light)',
      borderRadius: '16px', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '20px', borderBottom: '1px solid var(--border-subtle)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <h3 style={{ color: 'var(--text-primary)', fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>
            Daily Itinerary Planner
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '4px 0 0' }}>
            {destination ? `${destination} — ` : ''}{tripDays} day{tripDays > 1 ? 's' : ''}
          </p>
        </div>
        <button data-testid="save-itinerary-btn" onClick={handleSave} style={{
          padding: '8px 20px', background: 'var(--gold-primary)', color: 'var(--btn-primary-text)',
          border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
        }}>
          Save Plan
        </button>
      </div>

      {/* Day tabs */}
      <div style={{
        display: 'flex', overflowX: 'auto', gap: '4px', padding: '12px 16px',
        borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface)',
      }}>
        {days.map((day, idx) => (
          <button
            key={idx}
            data-testid={`day-tab-${idx}`}
            onClick={() => setActiveDay(idx)}
            style={{
              padding: '8px 18px', borderRadius: '8px', whiteSpace: 'nowrap',
              fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
              border: '1px solid',
              background: activeDay === idx ? 'var(--gold-primary)' : 'transparent',
              color: activeDay === idx ? 'var(--btn-primary-text)' : 'var(--text-muted)',
              borderColor: activeDay === idx ? 'var(--gold-primary)' : 'var(--border-subtle)',
            }}
          >
            Day {day.dayNumber}
            {day.items.length > 0 && (
              <span style={{
                marginLeft: '6px', background: activeDay === idx ? 'rgba(0,0,0,0.15)' : 'var(--bg-elevated)',
                padding: '1px 7px', borderRadius: '10px', fontSize: '0.75rem',
              }}>{day.items.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Day content */}
      <div style={{ padding: '16px', minHeight: '200px' }}>
        {days[activeDay]?.items.length === 0 && !showAddForm ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-dim)" strokeWidth="1.5" strokeLinecap="round">
              <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <p style={{ color: 'var(--text-muted)', margin: '12px 0 0', fontSize: '0.9rem' }}>
              No activities planned for Day {days[activeDay]?.dayNumber}
            </p>
            <button
              data-testid="add-first-activity-btn"
              onClick={() => setShowAddForm(true)}
              style={{
                marginTop: '12px', padding: '10px 24px', background: 'var(--gold-primary)',
                color: 'var(--btn-primary-text)', border: 'none', borderRadius: '10px',
                fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
              }}
            >
              + Add Activity
            </button>
          </div>
        ) : (
          <>
            {/* Activity list */}
            <div style={{ display: 'grid', gap: '8px', marginBottom: '12px' }}>
              {days[activeDay]?.items.map((item, idx) => {
                const typeInfo = getTypeInfo(item.type);
                return (
                  <div key={item.id} data-testid={`activity-item-${item.id}`} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 14px', background: 'var(--bg-surface)',
                    borderRadius: '10px', border: '1px solid var(--border-subtle)',
                    borderLeft: `3px solid ${typeInfo.color}`,
                  }}>
                    {/* Time */}
                    <div style={{
                      color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600,
                      minWidth: '50px', textAlign: 'center',
                    }}>
                      {item.time}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1 }}>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>
                        {item.name}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600,
                          background: `${typeInfo.color}20`, color: typeInfo.color,
                        }}>
                          {typeInfo.label}
                        </span>
                        <span style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>{item.duration}</span>
                        {item.notes && <span style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>— {item.notes}</span>}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button onClick={() => moveActivity(activeDay, idx, -1)} disabled={idx === 0} style={{
                        background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '0.9rem',
                        opacity: idx === 0 ? 0.3 : 1,
                      }}>&uarr;</button>
                      <button onClick={() => moveActivity(activeDay, idx, 1)} disabled={idx === days[activeDay].items.length - 1} style={{
                        background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '0.9rem',
                        opacity: idx === days[activeDay].items.length - 1 ? 0.3 : 1,
                      }}>&darr;</button>
                      <button data-testid={`remove-activity-${item.id}`} onClick={() => removeActivity(activeDay, item.id)} style={{
                        background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.9rem',
                      }}>&times;</button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add more button */}
            {!showAddForm && (
              <button
                data-testid="add-activity-btn"
                onClick={() => setShowAddForm(true)}
                style={{
                  width: '100%', padding: '10px', background: 'transparent',
                  border: '1px dashed var(--border-medium)', borderRadius: '10px',
                  color: 'var(--gold-primary)', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                }}
              >
                + Add Activity
              </button>
            )}
          </>
        )}

        {/* Add form */}
        {showAddForm && (
          <div data-testid="add-activity-form" style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border-light)',
            borderRadius: '12px', padding: '16px', marginTop: '8px',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>Time</label>
                <select
                  data-testid="activity-time"
                  value={newItem.time}
                  onChange={e => setNewItem(p => ({ ...p, time: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px', background: 'var(--bg-card)',
                    color: 'var(--text-primary)', border: '1px solid var(--border-light)',
                    borderRadius: '8px', fontSize: '0.9rem',
                  }}
                >
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>Activity</label>
                <input
                  data-testid="activity-name-input"
                  type="text"
                  placeholder="e.g. Visit Eiffel Tower"
                  value={newItem.name}
                  onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px 10px', background: 'var(--bg-card)',
                    color: 'var(--text-primary)', border: '1px solid var(--border-light)',
                    borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
              <div>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>Type</label>
                <select
                  data-testid="activity-type"
                  value={newItem.type}
                  onChange={e => setNewItem(p => ({ ...p, type: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px', background: 'var(--bg-card)',
                    color: 'var(--text-primary)', border: '1px solid var(--border-light)',
                    borderRadius: '8px', fontSize: '0.85rem',
                  }}
                >
                  {ACTIVITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>Duration</label>
                <select
                  data-testid="activity-duration"
                  value={newItem.duration}
                  onChange={e => setNewItem(p => ({ ...p, duration: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px', background: 'var(--bg-card)',
                    color: 'var(--text-primary)', border: '1px solid var(--border-light)',
                    borderRadius: '8px', fontSize: '0.85rem',
                  }}
                >
                  {['30 min', '1 hour', '1.5 hours', '2 hours', '3 hours', '4 hours', 'Half day', 'Full day'].map(d =>
                    <option key={d} value={d}>{d}</option>
                  )}
                </select>
              </div>
              <div>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>Notes</label>
                <input
                  data-testid="activity-notes"
                  type="text"
                  placeholder="Optional"
                  value={newItem.notes}
                  onChange={e => setNewItem(p => ({ ...p, notes: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px 10px', background: 'var(--bg-card)',
                    color: 'var(--text-primary)', border: '1px solid var(--border-light)',
                    borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAddForm(false)} style={{
                padding: '8px 16px', background: 'transparent', border: '1px solid var(--border-light)',
                color: 'var(--text-muted)', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem',
              }}>Cancel</button>
              <button
                data-testid="confirm-add-activity"
                onClick={addActivity}
                disabled={!newItem.name.trim()}
                style={{
                  padding: '8px 20px', background: 'var(--gold-primary)',
                  color: 'var(--btn-primary-text)', border: 'none', borderRadius: '8px',
                  fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                  opacity: !newItem.name.trim() ? 0.5 : 1,
                }}
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
